import MailboxTableHeader from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxTableHeader';
import { DateRangeFilter } from '@src/main/modules/messages/utils/templates';
import { useIncapacitatedReports } from '@src/main/modules/reports/incapacitated/application/useIncapacitatedReports';
import { IncapacitatedReportCriteria } from '@src/main/modules/reports/incapacitated/domain/incapacitated-report.interface';
import {
  getCarrierCodeColumnProps,
  getCreatedAtColumnProps,
  getDepartureAirportColumnProps,
  getFlightDateColumnProps,
  getFlightNumberColumnProps,
  getIncapacitatedByColumnProps,
  getIncapacitatedEmployeeIdColumnProps,
  getSeatAssignedColumnProps,
} from '@src/main/modules/reports/ui/components/table/ReportsColumns';
import ReportsTable from '@src/main/modules/reports/ui/components/table/ReportsTable';
import InvalidDateRangeDialog from '@src/main/shared/ui/components/calendar/InvalidDateRangeDialog';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import { getThreeMonthDateRange, toLocalISODate } from '@src/main/shared/utils/dateUtils';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import {
  DataTableFilterMeta,
  DataTableFilterMetaData,
  DataTableOperatorFilterMetaData,
} from 'primereact/datatable';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../ui/components/PunctualityView.module.scss';

const makeInitialFilters = (startDate: Date, endDate: Date): DataTableFilterMeta => ({
  flightDate: {
    operator: FilterOperator.AND,
    constraints: [{ value: [startDate, endDate], matchMode: FilterMatchMode.BETWEEN }],
  },
  flightNumber: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  departureAirport: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  incapacitatedEmployeeId: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  incapacitatedBy: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
});

const getFilterValue = (filter?: DataTableFilterMetaData | DataTableOperatorFilterMetaData) => {
  if (!filter) return null;
  const value = 'constraints' in filter ? filter.constraints?.[0]?.value : filter.value;
  return typeof value === 'string' && value.trim() ? value.trim().toUpperCase() : null;
};

const IncapacitatedReportsView: FC = () => {
  const { t } = useTranslation();
  const { startDate, endDate } = useMemo(() => getThreeMonthDateRange(), []);
  const initialFilters = useMemo(
    () => makeInitialFilters(startDate, endDate),
    [startDate, endDate],
  );
  const [filters, setFilters] = useState<DataTableFilterMeta>(initialFilters);
  const [invalidDateModalVisible, setInvalidDateModalVisible] = useState(false);

  const criteria = useMemo<IncapacitatedReportCriteria>(() => {
    const dateFilter = filters.flightDate;
    const range =
      dateFilter && 'constraints' in dateFilter ? dateFilter.constraints?.[0]?.value : null;
    const employeeId = getFilterValue(filters.incapacitatedEmployeeId);
    const incapacitatedBy = getFilterValue(filters.incapacitatedBy);

    return {
      flightNumber: getFilterValue(filters.flightNumber),
      departureAirport: getFilterValue(filters.departureAirport),
      arrivalAirport: null,
      aircraftRegistration: null,
      payroll: employeeId ?? incapacitatedBy,
      flightSince: Array.isArray(range) && range[0] ? toLocalISODate(range[0]) : null,
      flightUntil: Array.isArray(range) && range[1] ? toLocalISODate(range[1]) : null,
      sortField: 'flightDate',
      sortOrder: 'desc',
      offset: 0,
      limit: 10,
    };
  }, [filters]);

  const {
    reports,
    total,
    isLoading,
    isFetching,
    refetch,
    pageNumber,
    pageSize,
    sortField,
    sortOrder,
    setPageNumber,
    handlePageChange,
    handleSort,
  } = useIncapacitatedReports(criteria);

  const dateFilterTemplate = useCallback(
    (options: ColumnFilterElementTemplateOptions) => (
      <DateRangeFilter
        value={options.value as (Date | null)[] | null}
        onChange={(value) => {
          if (value?.[0] && value?.[1] && value[0] > value[1]) {
            setInvalidDateModalVisible(true);
            options.filterCallback(null, options.index);
            return;
          }
          options.filterCallback(value, options.index);
        }}
        t={t}
      />
    ),
    [t],
  );

  const handleFilters = (newFilters: DataTableFilterMeta) => {
    setFilters(newFilters);
    setPageNumber(0);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setPageNumber(0);
  };

  const header = <MailboxTableHeader onRefresh={refetch} onClearFilters={clearFilters} />;

  return (
    <div className={styles.container}>
      <AppLoading isLoading={isLoading || isFetching}>
        <ReportsTable
          data={reports}
          filters={filters}
          setFilters={handleFilters}
          header={header}
          loading={isLoading || isFetching}
          dataKey="id"
          totalRecords={total}
          first={pageNumber * pageSize}
          rows={pageSize}
          onPage={handlePageChange}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
        >
          <Column {...getFlightDateColumnProps(t, dateFilterTemplate)} />
          <Column {...getCarrierCodeColumnProps(t)} filter={false} />
          <Column {...getFlightNumberColumnProps(t)} />
          <Column {...getDepartureAirportColumnProps(t)} />
          <Column {...getIncapacitatedEmployeeIdColumnProps(t)} />
          <Column {...getSeatAssignedColumnProps(t)} />
          <Column {...getIncapacitatedByColumnProps(t)} />
          <Column {...getCreatedAtColumnProps(t)} />
        </ReportsTable>
      </AppLoading>

      <InvalidDateRangeDialog
        invalidDateRangeModalVisible={invalidDateModalVisible}
        setInvalidDateRangeModalVisible={setInvalidDateModalVisible}
        t={t}
      />
    </div>
  );
};

export default IncapacitatedReportsView;
